/**
 * Perspective transform: map a quad in the source image to a rectangle (e.g. NID card aspect).
 * Uses DLT homography + inverse bilinear sampling (no extra deps).
 */

/** @typedef {[number, number]} Pt */

/**
 * Solve 8×8 system A x = b (Gauss–Jordan).
 * @param {number[][]} A 8×8
 * @param {number[]} b length 8
 */
export function solveLinear8(A, b) {
    const n = 8;
    const M = A.map((row, i) => [...row, b[i]]);

    for (let i = 0; i < n; i++) {
        let piv = i;
        for (let k = i + 1; k < n; k++) {
            if (Math.abs(M[k][i]) > Math.abs(M[piv][i])) piv = k;
        }
        if (Math.abs(M[piv][i]) < 1e-12) {
            throw new Error("singular");
        }
        [M[i], M[piv]] = [M[piv], M[i]];
        const div = M[i][i];
        for (let j = i; j <= n; j++) M[i][j] /= div;
        for (let k = 0; k < n; k++) {
            if (k === i) continue;
            const f = M[k][i];
            for (let j = i; j <= n; j++) M[k][j] -= f * M[i][j];
        }
    }
    return M.map((row) => row[n]);
}

/**
 * Homography H (3×3, h33=1) mapping source plane points → destination plane points:
 * [xd, yd, wd]' ~ H [xs, ys, 1]'
 * @param {Pt[]} srcQuad [TL, TR, BR, BL] in source pixels
 * @param {Pt[]} dstQuad [TL, TR, BR, BL] in destination pixels
 * @returns {number[][]} 3×3 matrix
 */
export function homographyFromQuadToQuad(srcQuad, dstQuad) {
    const A = Array.from({ length: 8 }, () => Array(8).fill(0));
    const b = Array(8).fill(0);
    for (let i = 0; i < 4; i++) {
        const [x, y] = srcQuad[i];
        const [X, Y] = dstQuad[i];
        A[2 * i][0] = x;
        A[2 * i][1] = y;
        A[2 * i][2] = 1;
        A[2 * i][6] = -X * x;
        A[2 * i][7] = -X * y;
        b[2 * i] = X;
        A[2 * i + 1][3] = x;
        A[2 * i + 1][4] = y;
        A[2 * i + 1][5] = 1;
        A[2 * i + 1][6] = -Y * x;
        A[2 * i + 1][7] = -Y * y;
        b[2 * i + 1] = Y;
    }
    const h = solveLinear8(A, b);
    return [
        [h[0], h[1], h[2]],
        [h[3], h[4], h[5]],
        [h[6], h[7], 1],
    ];
}

/** @param {number[][]} M 3×3 (row-major) */
export function invert3x3(M) {
    const m = M;
    const det =
        m[0][0] * (m[1][1] * m[2][2] - m[2][1] * m[1][2]) -
        m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
        m[0][2] * (m[1][0] * m[2][1] - m[2][0] * m[1][1]);
    if (Math.abs(det) < 1e-14) throw new Error("singular");
    const s = 1 / det;
    return [
        [
            (m[1][1] * m[2][2] - m[2][1] * m[1][2]) * s,
            (m[0][2] * m[2][1] - m[0][1] * m[2][2]) * s,
            (m[0][1] * m[1][2] - m[0][2] * m[1][1]) * s,
        ],
        [
            (m[1][2] * m[2][0] - m[1][0] * m[2][2]) * s,
            (m[0][0] * m[2][2] - m[0][2] * m[2][0]) * s,
            (m[1][0] * m[0][2] - m[0][0] * m[1][2]) * s,
        ],
        [
            (m[1][0] * m[2][1] - m[2][0] * m[1][1]) * s,
            (m[2][0] * m[0][1] - m[0][0] * m[2][1]) * s,
            (m[0][0] * m[1][1] - m[1][0] * m[0][1]) * s,
        ],
    ];
}

/** Apply 3×3 H to homogeneous point (u,v,1); return Cartesian image coords. */
function mulMatVec(M, u, v) {
    const w = M[2][0] * u + M[2][1] * v + M[2][2];
    const px = M[0][0] * u + M[0][1] * v + M[0][2];
    const py = M[1][0] * u + M[1][1] * v + M[1][2];
    if (Math.abs(w) < 1e-12) return [NaN, NaN];
    return [px / w, py / w];
}

function sampleBilinear(data, width, height, x, y) {
    if (x < 0 || y < 0 || x >= width - 1 || y >= height - 1) {
        const xi = Math.max(0, Math.min(width - 1, Math.floor(x)));
        const yi = Math.max(0, Math.min(height - 1, Math.floor(y)));
        const i = (yi * width + xi) * 4;
        return [data[i], data[i + 1], data[i + 2], data[i + 3]];
    }
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const x1 = x0 + 1;
    const y1 = y0 + 1;
    const fx = x - x0;
    const fy = y - y0;
    const idx = (yy, xx) => ((yy * width + xx) * 4);
    const i00 = idx(y0, x0);
    const i10 = idx(y0, x1);
    const i01 = idx(y1, x0);
    const i11 = idx(y1, x1);
    const out = [0, 0, 0, 0];
    for (let c = 0; c < 4; c++) {
        const v00 = data[i00 + c];
        const v10 = data[i10 + c];
        const v01 = data[i01 + c];
        const v11 = data[i11 + c];
        out[c] =
            (1 - fx) * (1 - fy) * v00 + fx * (1 - fy) * v10 + (1 - fx) * fy * v01 + fx * fy * v11;
    }
    return out;
}

/**
 * Perspective warp image → canvas (destination is axis-aligned rectangle).
 * @param {HTMLImageElement | HTMLCanvasElement} source
 * @param {Pt} srcQuad [TL, TR, BR, BL] in **source pixel** coordinates
 * @param {number} outW
 * @param {number} outH
 */
export function warpQuadToRect(source, srcQuad, outW, outH) {
    const w = source.naturalWidth || source.width;
    const h = source.naturalHeight || source.height;
    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = w;
    srcCanvas.height = h;
    const sctx = srcCanvas.getContext("2d");
    if (!sctx) throw new Error("canvas");
    sctx.drawImage(source, 0, 0);
    const srcData = sctx.getImageData(0, 0, w, h).data;

    const dstQuad = [
        [0, 0],
        [outW, 0],
        [outW, outH],
        [0, outH],
    ];
    const H = homographyFromQuadToQuad(srcQuad, dstQuad);
    const Hinv = invert3x3(H);

    const outCanvas = document.createElement("canvas");
    outCanvas.width = outW;
    outCanvas.height = outH;
    const octx = outCanvas.getContext("2d");
    if (!octx) throw new Error("canvas");
    const outImage = octx.createImageData(outW, outH);

    for (let v = 0; v < outH; v++) {
        for (let u = 0; u < outW; u++) {
            const [sx, sy] = mulMatVec(Hinv, u, v);
            if (sx < -1 || sy < -1 || sx > w || sy > h) {
                const i = (v * outW + u) * 4;
                outImage.data[i] = 255;
                outImage.data[i + 1] = 255;
                outImage.data[i + 2] = 255;
                outImage.data[i + 3] = 255;
                continue;
            }
            const [r, g, b, a] = sampleBilinear(srcData, w, h, sx, sy);
            const i = (v * outW + u) * 4;
            outImage.data[i] = r;
            outImage.data[i + 1] = g;
            outImage.data[i + 2] = b;
            outImage.data[i + 3] = a;
        }
    }
    octx.putImageData(outImage, 0, 0);
    return outCanvas;
}

/**
 * Put four corners into the canonical TL, TR, BR, BL order used by every warp.
 *
 * The warp maps corner 0 to the output's top-left, so the *order* of the array
 * decides the output's orientation, not where the points sit on screen. A quad
 * that is merely cycled — [TR, BR, BL, TL] — warps to a picture turned 90°.
 * Sorting by angle around the centroid gives a consistent winding whatever the
 * points' history, and starting that cycle at the point nearest the image
 * origin pins corner 0 to the visually top-left one.
 *
 * @param {Pt[]} quad length 4
 * @returns {Pt[]} same points, canonically ordered
 */
export function orderQuadCorners(quad) {
    if (!Array.isArray(quad) || quad.length !== 4) return quad;
    const cx = (quad[0][0] + quad[1][0] + quad[2][0] + quad[3][0]) / 4;
    const cy = (quad[0][1] + quad[1][1] + quad[2][1] + quad[3][1]) / 4;
    // Screen axes point down, so ascending atan2 walks the quad clockwise.
    const ring = quad
        .map((p) => ({ p, a: Math.atan2(p[1] - cy, p[0] - cx) }))
        .sort((m, n) => m.a - n.a)
        .map((m) => m.p);
    let start = 0;
    let best = Infinity;
    ring.forEach((p, i) => {
        const s = p[0] + p[1];
        if (s < best) {
            best = s;
            start = i;
        }
    });
    return [0, 1, 2, 3].map((i) => ring[(start + i) % 4]);
}

/**
 * Simple convex test: consecutive edge turns share the same winding (TL→TR→BR→BL).
 * @param {Pt[]} quad length 4
 */
export function isConvexQuad(quad) {
    let sign = 0;
    for (let i = 0; i < 4; i++) {
        const p0 = quad[i];
        const p1 = quad[(i + 1) % 4];
        const p2 = quad[(i + 2) % 4];
        const v1 = [p1[0] - p0[0], p1[1] - p0[1]];
        const v2 = [p2[0] - p1[0], p2[1] - p1[1]];
        const z = v1[0] * v2[1] - v1[1] * v2[0];
        if (Math.abs(z) < 1e-9) return false;
        const s = z > 0 ? 1 : -1;
        if (sign === 0) sign = s;
        else if (s !== sign) return false;
    }
    return true;
}
